import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getProductImageUrl, IMAGE_SIZES } from 'utils/productImages';
import {
  classIfItemIsChosen,
  getVideoUrl,
  isVideo,
  shouldGoLeft,
  shouldGoRight,
  stopVideo,
  unifyTouchEvents,
} from 'apps/products/utils/bopGallery';

import { wa } from 'apps/analytics';
import { pageEventFields, pageEvents } from 'apps/products/analytics';

import BopGalleryMiniatures from '../../containers/BopGalleryMiniatures';
import ImageGalleryZoom from '../ImageGalleryZoom';

import { scrollToShelfLife } from '../../utils/createReview';
import Badges from '../partials/Badges';
import PackInfoShelfLife from 'apps/fops/components/fopPartials/PackInfoShelfLife';

export default class BopGallery extends PureComponent {
  static propTypes = {
    sku: PropTypes.string,
    images: PropTypes.array,
    videos: PropTypes.array,
    product: PropTypes.object,
    packInfo: PropTypes.object,
  };

  state = {
    shownItemIndex: 0,
    dragSize: null,
  };

  componentDidMount() {
    this.wrapper = document.querySelector('.bop-galleryWrapper');
    this.wrapperWidth = this.wrapper.clientWidth;

    this.wrapper.addEventListener('mousedown', this.lock);
    this.wrapper.addEventListener('touchstart', this.lock);

    this.wrapper.addEventListener('mousemove', this.drag, false);
    this.wrapper.addEventListener('touchmove', this.drag, false);

    this.wrapper.addEventListener('mouseup', this.move);
    this.wrapper.addEventListener('touchend', this.move);
  }

  componentWillUnmount() {
    this.wrapper.removeEventListener('mousedown', this.lock);
    this.wrapper.removeEventListener('touchstart', this.lock);

    this.wrapper.removeEventListener('mousemove', this.drag, false);
    this.wrapper.removeEventListener('touchmove', this.drag, false);

    this.wrapper.removeEventListener('mouseup', this.move);
    this.wrapper.removeEventListener('touchend', this.move);
  }

  cursorPosition = null;

  items = [];

  mainImagesList = [];

  lock = (e) => {
    this.cursorPosition = unifyTouchEvents(e).clientX;
  };

  drag = (e) => {
    if (this.cursorPosition) {
      this.setState({
        dragSize: Math.round(unifyTouchEvents(e).clientX - this.cursorPosition),
      });
    }
  };

  move = (e) => {
    const { shownItemIndex } = this.state;
    const numberOfItems = this.items.length;
    if (this.cursorPosition || this.cursorPosition === 0) {
      const deltaX = unifyTouchEvents(e).clientX - this.cursorPosition;
      const direction = Math.sign(deltaX);
      const dragToImageRatio = Math.abs(deltaX / this.wrapperWidth).toFixed(2);

      if ((shouldGoRight(shownItemIndex, direction)
        || shouldGoLeft(shownItemIndex, numberOfItems, direction))
        && dragToImageRatio > 0.15) {
        this.setMainImage(shownItemIndex - direction);
      }
      this.cursorPosition = null;
      this.setState({ dragSize: 0 });
    }
  };

  showModal = () => {
    this.setState({ showModal: true });
  };

  hideModal = () => {
    this.setState({ showModal: false });
  };

  setMainImage = (position) => {
    if (position === this.state.shownItemIndex) {
      return;
    }

    const currentItem = this.items[this.state.shownItemIndex];
    if (isVideo(currentItem)) {
      stopVideo(this.state.shownItemIndex);
    }

    const selectedItem = this.items[position];
    this.setState({
      shownItemIndex: position,
      dragSize: 0,
    }, () => {
      if (selectedItem && !isVideo(selectedItem)) {
        wa.triggerPageEventWithDescription(
          pageEvents.BOP_IMAGE_SELECTED,
          {
            [pageEventFields.IMG_HASH]: selectedItem.hash,
            [pageEventFields.IMG_POSITION]: position,
          });
      }
    });
  };

  renderItems = () => {
    const { sku } = this.props;
    const { shownItemIndex } = this.state;

    return this.items.map((item, index) => {
      if (isVideo(item)) {
        const videoSrc = getVideoUrl(item);

        if (videoSrc) {
          /* ToDo: WEBSHOP-25450 - implement video component to use source API */
          return (
            <div
              key={`bop-gallery__item__${index}`} // eslint-disable-line
              className={`bop-gallery__item ${classIfItemIsChosen(shownItemIndex, index, 'item')}`}
            >
              <iframe
                className="bop-gallery__video"
                title={`${item.videoType}-${item.embedId}`}
                src={videoSrc}
                allowFullScreen
              />
            </div>
          );
        }

        return null;
      }

      return (
        <div
          key={`bop-gallery__item__${index}`} // eslint-disable-line
          className={`bop-gallery__item ${classIfItemIsChosen(shownItemIndex, index, 'item')} bop-gallery__itemImage`}
        >
          <img
            role="presentation"
            className="bop-gallery__image"
            src={getProductImageUrl(sku, item, IMAGE_SIZES.S_640)}
            alt={item.name}
            ref={imageRef => {
              this.mainImagesList.push(imageRef);
            }}
            onClick={this.showModal}
          />
        </div>
      );
    });
  };

  render() {
    const { sku, images = [], videos = [], packInfo = {}, product = {}, betaFeatures = [] } = this.props;
    const hasPackInfo = Object.keys(packInfo).length > 0;
    this.items = [...images, ...videos];

    return (
      <div
        className={
          classNames('bop-galleryWrapper',
            { 'bop-galleryWrapper__addedSpace': this.items.length > 1 },
            { 'bop-galleryWrapper__withPackInfo': hasPackInfo }
          )
        }
        style={{ '--bop-gallery-shownItemIndex': this.state.shownItemIndex, '--dragSize': this.state.dragSize }}
      >
        {this.renderItems()}
        {hasPackInfo && (
          <div
            role="presentation"
            className="bop-packInfo__wrapper"
            onClick={scrollToShelfLife}
          >
            <Badges product={product} showFPBadge={betaFeatures.includes('FOODMAESTRO_ENABLED')} />
            <PackInfoShelfLife packInfo={packInfo} className="bop-shelfLife" />
          </div>
        )}
        <BopGalleryMiniatures
          items={this.items}
          onClick={this.setMainImage}
          shownItemIndex={this.state.shownItemIndex}
          sku={sku}
        />
        {this.state.showModal && (
          <ImageGalleryZoom
            sku={sku}
            images={images}
            currentImageIndex={this.state.shownItemIndex}
            hideModal={this.hideModal}
          />
        )}
      </div>
    );
  }
}
